import { useState, useCallback, useEffect, useRef } from 'react'
import { likeOrDislike } from '../../../services/likes/likeOrDislike.service'
import { getLikesByFirmId } from '../../../services/likes/getLikesByFirmId.service'
import type { Like, LikeOrDislikeResponse } from '../../../types/Like'

interface UseLikeProps {
  firmId: number
  frontUuid: string
  fingerprint: string
  initialLikesCount?: number
  initialIsLiked?: boolean
}

interface UseLikeReturn {
  isLiked: boolean
  likesCount: number
  isLoading: boolean
  toggleLike: () => Promise<void>
}

/**
 * Hook to manage like functionality for a firm
 * @param props - The props for the hook
 * @param props.firmId - The ID of the firm to like/unlike
 * @param props.frontUuid - The UUID of the user performing the action
 * @param props.fingerprint - The visitor ID of the user
 * @param props.initialLikesCount - Optional initial count of likes
 * @param props.initialIsLiked - Optional initial liked state
 * @returns An object containing the like state and functions
 */
export function useLike({
  firmId,
  frontUuid,
  fingerprint,
  initialLikesCount = 0,
  initialIsLiked = false,
}: UseLikeProps): UseLikeReturn {
  const [isLiked, setIsLiked] = useState(initialIsLiked)
  const [likesCount, setLikesCount] = useState(initialLikesCount)
  const [isLoading, setIsLoading] = useState(false)
  const isMounted = useRef(true)
  const pendingToggle = useRef<Promise<LikeOrDislikeResponse> | null>(null)

  // Cleanup on unmount
  useEffect(() => () => {
    isMounted.current = false
  }, [])

  // Fetch initial likes state
  useEffect(() => {
    const fetchInitialLikes = async () => {
      try {
        const response = await getLikesByFirmId(firmId)
        if (!isMounted.current) return

        setLikesCount(response.count)
        // Check if the current user has liked this firm
        const userLike = response.likes.find(
          (like: Like) => like.frontUuid === frontUuid
        )
        setIsLiked(!!userLike)
      } catch (error) {
        console.error('Failed to fetch initial likes:', error)
      }
    }

    fetchInitialLikes()
  }, [firmId, frontUuid])

  const toggleLike = useCallback(async () => {
    if (isLoading || pendingToggle.current) return

    setIsLoading(true)
    const previousState = { isLiked, likesCount }

    // Optimistically update the UI
    setIsLiked(!isLiked)
    setLikesCount((prev) => (isLiked ? prev - 1 : prev + 1))

    try {
      pendingToggle.current = likeOrDislike({
        firmId,
        frontUuid,
        fingerprint,
      })

      const response = await pendingToggle.current

      if (!isMounted.current) return

      setLikesCount(response.count)
      // Check if the current user has liked this firm
      const userLike = response.likes.find(
        (like: Like) => like.frontUuid === frontUuid
      )
      setIsLiked(!!userLike)
    } catch (error) {
      if (!isMounted.current) return

      // Revert optimistic update on error
      setIsLiked(previousState.isLiked)
      setLikesCount(previousState.likesCount)
      console.error('Failed to toggle like:', error)
    } finally {
      if (isMounted.current) {
        setIsLoading(false)
        pendingToggle.current = null
      }
    }
  }, [firmId, frontUuid, fingerprint, isLiked, isLoading])

  return { isLiked, likesCount, isLoading, toggleLike }
}
